import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Button,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  HelpOutline as InfoIcon,
  Comment as CommentIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { PurchaseRequest, RequestStatus } from '../../types/request.types';
import { useAppSelector } from '../../store';
import { requestService } from '../../services/requestService';
import { UserRole } from '../../types/auth.types';

interface ActionHistoryProps {
  onViewRequest: (request: PurchaseRequest) => void;
  onBack: () => void;
}

interface ActionHistoryRequest extends PurchaseRequest {
  userActions: Array<{
    type: 'APPROVED' | 'REJECTED' | 'REQUESTED_INFO' | 'COMMENTED' | 'MARKED_AS_PAID';
    date: string | Date;
    comment?: string | null;
  }>;
  lastActionDate: string | Date;
}

const ActionHistory: React.FC<ActionHistoryProps> = ({ onViewRequest, onBack }) => {
  const theme = useTheme();
  const [requests, setRequests] = useState<ActionHistoryRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    const fetchActionHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await requestService.getActionHistory();
        setRequests(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch action history');
      } finally {
        setLoading(false);
      }
    };

    if (user && [UserRole.DEPARTMENT_MANAGER, UserRole.GENERAL_MANAGER, UserRole.FINANCE_ADMIN, UserRole.SENIOR_MANAGER, UserRole.CFO].includes(user.role)) {
      fetchActionHistory();
    } else {
      setError('Access denied. Action history is only available for managers and finance users.');
      setLoading(false);
    }
  }, [user]);

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.DRAFT:
        return 'default';
      case RequestStatus.PENDING_MANAGER:
      case RequestStatus.PENDING_SENIOR_MANAGER:
      case RequestStatus.PENDING_GENERAL_MANAGER:
      case RequestStatus.PENDING_FINANCE:
      case RequestStatus.PENDING_CFO:
        return 'warning';
      case RequestStatus.MORE_INFO_NEEDED:
        return 'info';
      case RequestStatus.APPROVED:
        return 'success';
      case RequestStatus.REJECTED:
        return 'error';
      case RequestStatus.PAID:
      case RequestStatus.CLOSED:
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'APPROVED':
        return <ApprovedIcon color="success" />;
      case 'REJECTED':
        return <RejectedIcon color="error" />;
      case 'REQUESTED_INFO':
        return <InfoIcon color="info" />;
      case 'COMMENTED':
        return <CommentIcon color="primary" />;
      case 'MARKED_AS_PAID':
        return <ApprovedIcon color="secondary" />;
      default:
        return <CommentIcon color="disabled" />;
    }
  };

  const getActionColor = (actionType: string) => {
    switch (actionType) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'REQUESTED_INFO':
        return 'info';
      case 'COMMENTED':
        return 'primary';
      case 'MARKED_AS_PAID':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getActionDisplayText = (actionType: string) => {
    switch (actionType) {
      case 'APPROVED':
        return 'APPROVED';
      case 'REJECTED':
        return 'REJECTED';
      case 'REQUESTED_INFO':
        return 'REQUESTED INFO';
      case 'COMMENTED':
        return 'COMMENTED';
      case 'MARKED_AS_PAID':
        return 'MARKED AS PAID';
      default:
        return actionType.replace(/_/g, ' ');
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton 
            onClick={onBack}
            sx={{ 
              backgroundColor: 'primary.main', 
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
            My Action History
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Requests where you have taken action (approved, rejected, requested info, or commented)
        </Typography>

        {requests.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No action history found. You haven't taken any actions on requests yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Request Details</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Current Status</TableCell>
                  <TableCell>Your Actions</TableCell>
                  <TableCell>Last Action</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {request.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {request.requester.firstName} {request.requester.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.department.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(request.amount, request.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.status.replace(/_/g, ' ')}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {request.userActions.slice(0, 2).map((action, index) => (
                          <Chip
                            key={index}
                            icon={getActionIcon(action.type)}
                            label={getActionDisplayText(action.type)}
                            color={getActionColor(action.type) as any}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {request.userActions.length > 2 && (
                          <Chip
                            label={`+${request.userActions.length - 2} more`}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(request.lastActionDate), 'MMM dd, yyyy')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(request.lastActionDate), 'HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => onViewRequest(request)}
                        title="View Details"
                      >
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Detailed Action History */}
        {requests.length > 0 && (
          <Box mt={4}>
            <Typography variant="h6" gutterBottom>
              Detailed Action History
            </Typography>
            {requests.map((request) => (
              <Accordion key={`detail-${request.id}`} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Typography variant="subtitle1" fontWeight="bold">
                      {request.title}
                    </Typography>
                    <Chip
                      label={`${request.userActions.length} action${request.userActions.length !== 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {request.userActions.map((action, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.default',
                        }}
                      >
                        {getActionIcon(action.type)}
                        <Box flex={1}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {getActionDisplayText(action.type)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(action.date), 'MMM dd, yyyy HH:mm')}
                          </Typography>
                          {action.comment && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              "{action.comment}"
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ActionHistory; 